import { useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  BarChart,
  Bar,
  Legend,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { Activity, TrendingDown, Zap, Timer } from "lucide-react";
import { format, subDays, startOfDay, differenceInSeconds } from "date-fns";
import { de } from "date-fns/locale";

interface Session {
  id: string;
  session_id: string;
  started_at: string;
  completed_at: string | null;
  current_step: string;
}

interface Props {
  sessions: Session[];
}

const STEP_ORDER = ["intro", "personal", "property", "preferences", "results"];
const STEP_LABELS: Record<string, string> = {
  intro: "Start",
  personal: "Persönlich",
  property: "Immobilie",
  preferences: "Wünsche",
  results: "Ergebnisse",
};

const UsageMonitoring = ({ sessions }: Props) => {
  // Last 14 days timeline
  const timelineData = useMemo(() => {
    const days: { date: string; label: string; gestartet: number; abgeschlossen: number }[] = [];
    for (let i = 13; i >= 0; i--) {
      const day = startOfDay(subDays(new Date(), i));
      const next = startOfDay(subDays(new Date(), i - 1));
      const started = sessions.filter((s) => {
        const d = new Date(s.started_at);
        return d >= day && d < next;
      }).length;
      const completed = sessions.filter((s) => {
        if (!s.completed_at) return false;
        const d = new Date(s.completed_at);
        return d >= day && d < next;
      }).length;
      days.push({
        date: day.toISOString(),
        label: format(day, "dd.MM", { locale: de }),
        gestartet: started,
        abgeschlossen: completed,
      });
    }
    return days;
  }, [sessions]);

  // Funnel / Drop-off per step (max step reached)
  const funnelData = useMemo(() => {
    const reached: Record<string, number> = {
      personal: 0,
      property: 0,
      preferences: 0,
      results: 0,
    };
    sessions.forEach((s) => {
      const isCompleted = !!s.completed_at;
      const maxIdx = isCompleted
        ? STEP_ORDER.indexOf("results")
        : STEP_ORDER.indexOf(s.current_step);
      // Count this session for every step up to its current step
      ["personal", "property", "preferences", "results"].forEach((step) => {
        if (STEP_ORDER.indexOf(step) <= maxIdx) reached[step]++;
      });
    });
    return [
      { step: "Schritt 1\nPersönlich", users: reached.personal },
      { step: "Schritt 2\nImmobilie", users: reached.property },
      { step: "Schritt 3\nWünsche", users: reached.preferences },
      { step: "Ergebnis", users: reached.results },
    ];
  }, [sessions]);

  // Drop-off distribution (where did users stop?)
  const dropOffData = useMemo(() => {
    const dropAt: Record<string, number> = {
      intro: 0,
      personal: 0,
      property: 0,
      preferences: 0,
      results: 0,
    };
    sessions.forEach((s) => {
      if (s.completed_at) {
        dropAt.results++;
      } else {
        dropAt[s.current_step] = (dropAt[s.current_step] || 0) + 1;
      }
    });
    return Object.entries(dropAt)
      .filter(([, v]) => v > 0)
      .map(([key, value]) => ({
        name: STEP_LABELS[key] || key,
        value,
      }));
  }, [sessions]);

  // Engagement metrics
  const engagement = useMemo(() => {
    const completed = sessions.filter((s) => s.completed_at);
    const durations = completed
      .map((s) =>
        differenceInSeconds(new Date(s.completed_at as string), new Date(s.started_at))
      )
      .filter((d) => d > 0 && d < 60 * 60 * 2); // ignore outliers > 2h

    const avgDuration = durations.length
      ? Math.round(durations.reduce((a, b) => a + b, 0) / durations.length)
      : 0;

    const startedWizard = sessions.filter((s) => s.current_step !== "intro" || s.completed_at).length;
    const interactionRate = sessions.length > 0
      ? Math.round((startedWizard / sessions.length) * 100)
      : 0;

    // Last 7 vs previous 7 days
    const now = new Date();
    const last7 = sessions.filter(
      (s) => new Date(s.started_at) >= subDays(now, 7)
    ).length;
    const prev7 = sessions.filter((s) => {
      const d = new Date(s.started_at);
      return d >= subDays(now, 14) && d < subDays(now, 7);
    }).length;
    const trend = prev7 > 0 ? Math.round(((last7 - prev7) / prev7) * 100) : last7 > 0 ? 100 : 0;

    return { avgDuration, interactionRate, last7, trend };
  }, [sessions]);

  const COLORS = [
    "hsl(var(--primary))",
    "hsl(var(--accent))",
    "hsl(var(--secondary))",
    "hsl(var(--muted-foreground))",
    "hsl(var(--destructive))",
  ];

  const formatDuration = (s: number) => {
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}m ${sec}s`;
  };

  return (
    <div className="space-y-6">
      {/* Engagement Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Letzte 7 Tage
            </CardTitle>
            <Activity className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{engagement.last7}</div>
            <p className={`text-xs mt-1 ${engagement.trend >= 0 ? "text-green-600" : "text-destructive"}`}>
              {engagement.trend >= 0 ? "+" : ""}{engagement.trend}% vs. Vorwoche
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Ø Dauer
            </CardTitle>
            <Timer className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatDuration(engagement.avgDuration)}</div>
            <p className="text-xs text-muted-foreground mt-1">bei abgeschlossenen Sessions</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Interaktionsrate
            </CardTitle>
            <Zap className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{engagement.interactionRate}%</div>
            <p className="text-xs text-muted-foreground mt-1">starten den Rechner</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Größter Drop-off
            </CardTitle>
            <TrendingDown className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {dropOffData
                .filter((d) => d.name !== "Ergebnisse")
                .sort((a, b) => b.value - a.value)[0]?.name || "-"}
            </div>
            <p className="text-xs text-muted-foreground mt-1">meistens hier abgebrochen</p>
          </CardContent>
        </Card>
      </div>

      {/* Timeline */}
      <Card>
        <CardHeader>
          <CardTitle>Nutzung über Zeit (14 Tage)</CardTitle>
          <CardDescription>Gestartete und abgeschlossene Beratungen pro Tag</CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={timelineData}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="label" stroke="hsl(var(--muted-foreground))" fontSize={12} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} allowDecimals={false} />
              <Tooltip
                contentStyle={{
                  backgroundColor: "hsl(var(--card))",
                  border: "1px solid hsl(var(--border))",
                  borderRadius: "0.5rem",
                }}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="gestartet"
                stroke="hsl(var(--primary))"
                strokeWidth={2}
                dot={{ r: 3 }}
              />
              <Line
                type="monotone"
                dataKey="abgeschlossen"
                stroke="hsl(var(--accent))"
                strokeWidth={2}
                dot={{ r: 3 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Funnel & Drop-off side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Funnel: Wie weit kommen Nutzer?</CardTitle>
            <CardDescription>Anzahl Nutzer, die jeden Schritt erreicht haben</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={funnelData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="step" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} allowDecimals={false} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: "0.5rem",
                  }}
                />
                <Bar dataKey="users" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Wo brechen Nutzer ab?</CardTitle>
            <CardDescription>Verteilung des letzten erreichten Schritts</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={dropOffData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${((percent || 0) * 100).toFixed(0)}%`}
                  outerRadius={90}
                  fill="hsl(var(--primary))"
                  dataKey="value"
                >
                  {dropOffData.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: "0.5rem",
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default UsageMonitoring;
